import sanitizeHtml from "sanitize-html";

import { getAccessoryMetafieldValue } from "@/components/Accessories/descriptionUtils";
import {
  MATIC_BAG_PASS_VARIANT_ID,
  MATIC_BUILDER_KIT_VARIANT_ID,
  MATIC_CARE_VARIANT_ID,
  PET_BRUSH_ROLL_VARIANT_ID,
} from "@/consts/shopify";
import type { Product } from "@/graphql/storefront/generated";

type ProductVariant = NonNullable<Product["variants"]["edges"][number]["node"]>;

export type AddOnId =
  "annual-bag-pass" | "matic-care" | "builder-kit" | "pet-brush-roll";

export type AddOnSpec = {
  id: AddOnId;
  variantId: string;
  /**
   * "per-matic" add-ons ship one unit per Matic in the floor package, so both
   * the price and the cart line scale with the Matic count; "flat" ones are a
   * single unit no matter how many Matics are ordered.
   */
  priceRule: "per-matic" | "flat";
  /**
   * Matic count at which the add-on becomes a free bundle inclusion: the card
   * locks to "Included", stops contributing to the total, and the line is sent
   * to the cart regardless of selection so the Shopify discount can zero it.
   */
  includedFromMaticCount?: number;
  /**
   * Whether a referral code that gives a free bag pass zeroes this add-on,
   * giving the card the same
   * "Included" treatment the Matic-count inclusion does. Required rather than
   * optional so a new add-on has to state its answer instead of inheriting one.
   */
  includedWithReferral: boolean;
};

export const ADD_ON_SPECS: readonly AddOnSpec[] = [
  {
    id: "annual-bag-pass",
    variantId: MATIC_BAG_PASS_VARIANT_ID,
    priceRule: "per-matic",
    includedFromMaticCount: 2,
    includedWithReferral: true,
  },
  {
    id: "matic-care",
    variantId: MATIC_CARE_VARIANT_ID,
    priceRule: "per-matic",
    includedWithReferral: false,
  },
  {
    id: "builder-kit",
    variantId: MATIC_BUILDER_KIT_VARIANT_ID,
    priceRule: "flat",
    includedWithReferral: false,
  },
  {
    id: "pet-brush-roll",
    variantId: PET_BRUSH_ROLL_VARIANT_ID,
    // It replaces the standard brush roll in each Matic, and the cart enforces
    // one per Matic (syncPerMaticAddOnsToMatics, raiseAddOnsWithMatics).
    priceRule: "per-matic",
    includedWithReferral: false,
  },
];

/**
 * One Shopify product per add-on. Every id is required so a new entry in
 * ADD_ON_SPECS cannot ship without its fetch being wired up.
 */
export type AddOnProducts = Record<AddOnId, Product | null>;

export type ResolvedAddOn = {
  id: AddOnId;
  priceRule: AddOnSpec["priceRule"];
  variantId: string;
  /** Product title and short description, both authored in Shopify. */
  name: string;
  description: string;
  /**
   * Shopify's full product description, sanitized to its paragraphs, lists
   * and emphasis, which the details dialog opens to; null when it says
   * nothing past the short description.
   */
  detailsHtml: string | null;
  /** Every Shopify photo, in order, for the details dialog; may be empty. */
  imageUrls: readonly string[];
  unitAmount: number;
  currencyCode: string;
  quantity: number;
  /** unitAmount * quantity, before the bundle inclusion is applied. */
  listAmount: number;
  /** What selecting the add-on adds to the total: 0 while it is included. */
  chargedAmount: number;
  isIncluded: boolean;
  availableForSale: boolean;
};

const getVariantById = (
  product: Product | null,
  variantId: string,
): ProductVariant | null =>
  product?.variants.edges.find(({ node }) => node.id === variantId)?.node ??
  null;

const getDescription = (product: Product): string =>
  getAccessoryMetafieldValue(product, "short_description") ??
  product.description ??
  "";

const DETAILS_TAGS = ["p", "br", "ul", "ol", "li", "strong", "em", "b", "i"];

const getDetailsHtml = (product: Product): string | null =>
  product.description.trim() === getDescription(product).trim()
    ? null
    : sanitizeHtml(product.descriptionHtml, {
        allowedTags: DETAILS_TAGS,
        allowedAttributes: {},
      });

/**
 * Turns the static specs plus the fetched products into the cards the
 * configurator can actually render: anything whose product failed to load or
 * whose configured variant is missing simply drops out.
 */
export const resolveAddOns = (
  products: AddOnProducts,
  maticCount: number,
  referralIncludesBagPass: boolean,
): ResolvedAddOn[] =>
  ADD_ON_SPECS.flatMap((spec) => {
    const product = products[spec.id];
    const variant = getVariantById(product, spec.variantId);

    if (product === null || variant === null) {
      return [];
    }

    const unitAmount = Number(variant.price.amount);

    if (!Number.isFinite(unitAmount)) {
      return [];
    }

    const quantity = spec.priceRule === "per-matic" ? maticCount : 1;
    const isIncluded =
      (spec.includedFromMaticCount !== undefined &&
        maticCount >= spec.includedFromMaticCount) ||
      (spec.includedWithReferral && referralIncludesBagPass);
    const listAmount = unitAmount * quantity;

    return [
      {
        id: spec.id,
        priceRule: spec.priceRule,
        variantId: spec.variantId,
        name: product.title,
        description: getDescription(product),
        detailsHtml: getDetailsHtml(product),
        imageUrls: product.images.edges.map(({ node }) => node.url),
        unitAmount,
        currencyCode: variant.price.currencyCode,
        quantity,
        listAmount,
        chargedAmount: isIncluded ? 0 : listAmount,
        isIncluded,
        availableForSale: variant.availableForSale,
      },
    ];
  });

/**
 * Cart lines for the add-ons the shopper picked, plus any bundle inclusions —
 * the included bag pass is still sent as a real line so the Shopify discount
 * function has something to zero out.
 */
export const getAddOnCartLines = (
  addOns: readonly ResolvedAddOn[],
  selectedIds: ReadonlySet<AddOnId>,
): { variantId: string; quantity: number }[] =>
  addOns
    .filter(
      (addOn) =>
        addOn.availableForSale &&
        (addOn.isIncluded || selectedIds.has(addOn.id)),
    )
    .map(({ variantId, quantity }) => ({ variantId, quantity }));

/**
 * What the selected add-ons add to the Add to Cart total. Mirrors the cart
 * line filter so the button price always equals what lands in the cart.
 */
export const getAddOnsAmount = (
  addOns: readonly ResolvedAddOn[],
  selectedIds: ReadonlySet<AddOnId>,
): number =>
  addOns.reduce(
    (total, addOn) =>
      addOn.availableForSale && selectedIds.has(addOn.id)
        ? total + addOn.chargedAmount
        : total,
    0,
  );
